import React, { useState } from 'react';
import classes from './Charges.module.scss';
import Input from '../../UI/Input/Input';
import Button from '../../UI/Button/Button';
import { Charge } from '../../../models/load';
import { FaTrash } from 'react-icons/fa';

interface ChargesProps {
  loadId: string
  charges: string
  subbieCharges?: string
  submitCharges: (charges: Charge[], loadId: string, chargeType?: 'client' | 'subbie') => void
  userType: string
  onClose?: () => void
};

const Charges: React.FC<ChargesProps> = (props) => {
  const [newCharge, setNewCharge] = useState<Charge>({ description: '', quantity: 1, amount: '' })
  const [charges, setCharges] = useState<Charge[]>(JSON.parse(props.charges || '[]'))
  const [subbieCharges, setSubbieCharges] = useState<Charge[]>(JSON.parse(props.subbieCharges || '[]'))
  const [activeChargeType, setActiveChargeType] = useState<'client' | 'subbie'>(props.userType === 'owner' ? 'subbie' : 'client')

  // For owner user type, always use subbieCharges
  const activeCharges = props.userType === 'admin'
    ? (activeChargeType === 'client' ? charges : subbieCharges)
    : props.userType === 'owner'
      ? subbieCharges
      : charges

  const setActiveCharges = props.userType === 'admin'
    ? (activeChargeType === 'client' ? setCharges : setSubbieCharges)
    : props.userType === 'owner'
      ? setSubbieCharges
      : setCharges

  const removeCharge = (index: number) => {
    setActiveCharges(activeCharges.filter((charge, i) => {
      return index !== i
    }))
  }

  const addCharge = () => {
    if (newCharge.description === '' || newCharge.amount === '') {
      return
    }

    setNewCharge({ description: '', quantity: 1, amount: '' })
    setActiveCharges(prevState => {
      const newCharges = [...prevState]
      const newAmount = parseFloat(newCharge.amount.split(' ')[1])

      newCharges.push({ ...newCharge, amount: newAmount.toFixed(0) })

      return newCharges
    })
  }

  const renderCharges = () => {
    return activeCharges.map((charge, index) => (
      <div className={classes.charge} key={index}>
        <div>{charge.description}</div>
        <div className='d-flex'>
          <div className='px-3'>{charge.quantity} x R {charge.amount}</div>

          {props.userType !== 'client' && props.userType !== 'owner' && (
            <div onClick={() => removeCharge(index)} className={classes.remove}><FaTrash /></div>
          )}
        </div>
      </div>
    ))
  }

  const updateNewCharge = (key: 'description' | 'quantity' | 'amount', value: string) => {
    setNewCharge(prevState => {
      const newState: Charge = { ...prevState }

      if (key === 'description') {
        newState.description = value
      } else if (key === 'quantity') {
        const intValue = parseInt(value)
        newState.quantity = intValue >= 1 ? intValue : 1
      } else if (key === 'amount') {
        newState.amount = value
      }

      return newState
    })
  }

  const calculateTotal = (chargesList: Charge[]): number => {
    return chargesList.reduce((sum, charge) => {
      const amount = parseFloat(charge.amount?.toString().replace(/[^0-9.-]+/g, '') || '0');
      const quantity = charge.quantity || 1;
      return sum + (isNaN(amount) ? 0 : amount * quantity);
    }, 0);
  }

  return (
    <div>
      {props.userType === 'admin' && (
        <div className={classes.chargeTypeTabs}>
          <div
            className={`${classes.chargeTypeTab} ${activeChargeType === 'client' ? classes.active : ''}`}
            onClick={() => setActiveChargeType('client')}
          >
            Client Additional Charges
          </div>
          <div
            className={`${classes.chargeTypeTab} ${activeChargeType === 'subbie' ? classes.active : ''}`}
            onClick={() => setActiveChargeType('subbie')}
          >
            Subbie Additional Charges
          </div>
        </div>
      )}

      <h6>{props.userType === 'admin'
        ? (activeChargeType === 'client' ? 'Additional Charges' : 'Subbie Additional Charges')
        : 'Additional Charges'}</h6>

      <div className={classes.charges}>
        {renderCharges()}
      </div>

      {activeCharges.length > 0 && (
        <div className={classes.totalCharges}>
          Total: R {calculateTotal(activeCharges).toFixed(2)}
        </div>
      )}

      {props.userType !== 'client' && props.userType !== 'owner' && (
        <div className={classes['new-charge']}>
          <Input
            elementType={'text'}
            elementConfig={{
              type: 'text',
              placeholder: 'Description',
            }}
            value={newCharge.description}
            change={(e) => updateNewCharge('description', e.target.value)}
            id='name-input'
            inputStyle='main'
            labelHidden
          />

          <div className={classes.quantity}>
            <Input
              elementType={'quantity'}
              elementConfig={{
                type: 'number',
                placeholder: 'Quantity',
              }}
              value={newCharge.quantity.toString()}
              change={(e) => updateNewCharge('quantity', e.target.value)}
              id='quantity-input'
              inputStyle='main'
              labelHidden
            />
          </div>

          <div className={classes.x}>
            x
          </div>

          <div className={classes.currency}>
            <Input
              elementType={'currency'}
              elementConfig={{
                placeholder: 'R 0.00',
              }}
              value={newCharge.amount}
              change={(e) => updateNewCharge('amount', e.target.value)}
              id='amount-input'
              inputStyle='main'
              labelHidden
            />
          </div>

          <div className='px-2'></div>

          <div className='d-flex align-items-center'>
            <Button
              type='button'
              buttonStyle='main'
              text='Add'
              click={addCharge}
            />
          </div>
        </div>
      )}

      {props.userType !== 'client' && props.userType !== 'owner' && (
        <div className='d-flex justify-content-center pt-3'>
          <div className={classes.cancel} onClick={props.onClose}>
            Cancel
          </div>

          <Button
            type='button'
            buttonStyle='main'
            text='Submit'
            click={() => {
              const newCharges = [...activeCharges]
              if (newCharge.description !== '' && newCharge.amount !== '') {
                const newAmount = parseFloat(newCharge.amount.split(' ')[1])
                newCharges.push({ ...newCharge, amount: newAmount.toFixed(0) })
              }
              props.submitCharges(
                newCharges,
                props.loadId,
                props.userType === 'admin' ? activeChargeType : props.userType === 'owner' ? 'subbie' : 'client'
              )
            }}
          />
        </div>
      )}

      {props.onClose && (props.userType === 'client' || props.userType === 'owner') && (
        <div className='d-flex justify-content-center pt-3 mt-2'>
          <Button
            type='button'
            buttonStyle='main'
            text='Close'
            click={props.onClose}
          />
        </div>
      )}
    </div>
  );
};

export default Charges;